import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { equipmentAPI } from '../../services/api'

/**
 * VH-126 Stage D: ask for an exercise for a machine nothing uses yet.
 *
 * Stage C surfaces the gap; this fills it. The proposal is a DRAFT — nothing is
 * saved until the user reviews it, which is why this component hands the result
 * upward rather than writing anything itself.
 *
 * Two warnings can come back with a proposal and both are deliberately
 * non-blocking:
 *
 *   muscle mismatch  — VH-125's cross-check. The equipment list says what the
 *                      TOOL can train; the exercise says what THIS MOVEMENT
 *                      trains. A mismatch is often correct, so it is a signal
 *                      for the user to judge, never a rule.
 *   duplicate        — the machine already has an exercise by that name. Worth
 *                      saying before saving, not worth forbidding.
 */
export default function ProposeExerciseButton({ equipment, onProposal }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Ask the server to use the photo it already has.
   *
   * This used to fetch `equipment.photo_url` in the browser and forward the
   * bytes as an upload, which failed for any of three reasons that all looked
   * identical to the user — the field is absent from the list payload the
   * caller passes in, the URL is cross-origin, or the object has moved — and
   * every failure fell through to the file picker. So a machine that plainly
   * HAS a picture kept demanding another one, and the scan-then-propose path
   * asked for the photo that had just been uploaded seconds earlier.
   *
   * The request now carries no file at all and the server reads its own
   * storage, which it is authoritative about. The picker is kept for the one
   * case that genuinely needs it: no usable stored image, signalled by a 400.
   */
  const start = async () => {
    setError(null)
    setBusy(true)
    await run(null)
  }

  const onFile = async (event) => {
    const file = event.target.files?.[0]
    // Reset first so the same photo can be retried after a failure.
    event.target.value = ''
    if (!file) return
    setBusy(true)
    setError(null)
    await run(file)
  }

  const run = async (file) => {
    try {
      const { data } = await equipmentAPI.proposeExercise(equipment.id, file)
      if (!data.proposal) {
        // Not an error: the model looked and found no usable movement. Saying
        // so beats inventing an exercise.
        setError(t('trainingLocations.propose.nothingUsable'))
        return
      }
      onProposal?.(data)
    } catch (err) {
      // 400 means the server has no usable stored photo. That is the one case
      // where asking the user for one is the right answer rather than an
      // apology — so open the picker instead of showing an error. Only reached
      // when no file was sent; a 400 on an actual upload is a real failure.
      if (err?.response?.status === 400 && !file) {
        setBusy(false)
        inputRef.current?.click()
        return
      }
      setError(
        err?.response?.data?.detail || t('trainingLocations.propose.failed')
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid={`propose-${equipment.id}`}>
      <button
        type="button"
        onClick={start}
        disabled={busy}
        className="btn-outline btn-sm flex items-center disabled:opacity-50"
        data-testid={`propose-button-${equipment.id}`}
      >
        <SparklesIcon className="mr-1 h-4 w-4" />
        {busy
          ? t('trainingLocations.propose.thinking')
          : t('trainingLocations.propose.action')}
      </button>

      {/* Always available: the stored photo may be a poor angle for judging a
          movement even when it is fine for recognising the machine. */}
      {equipment.photo_url && !busy && (
        <button
          type="button"
          onClick={() => { setError(null); inputRef.current?.click() }}
          className="mt-1 block text-xs text-primary-600 hover:text-primary-700"
          data-testid={`propose-newphoto-${equipment.id}`}
        >
          {t('trainingLocations.propose.useNewPhoto')}
        </button>
      )}

      {error && (
        <p
          className="mt-1 text-xs text-amber-700"
          role="alert"
          data-testid={`propose-error-${equipment.id}`}
        >
          {error}
        </p>
      )}

      {/* A photo grounds the proposal in the actual machine rather than its
          name alone — "Rear Kick Machine" tells a model very little. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif"
        onChange={onFile}
        className="hidden"
        data-testid={`propose-input-${equipment.id}`}
      />
    </div>
  )
}
